/**
 * Sunday Sports Reviews — PDP widget + inline badge.
 *
 * Two custom elements:
 *   <ss-reviews-widget product-id="..."></ss-reviews-widget>
 *   <ss-review-badge product-id="..."></ss-review-badge>
 *
 * Data layer is in assets/reviews-api.js (mocked while backend is in flight).
 */

(function () {
const { fetchGlobalSummary, fetchSummary, fetchSummaryBatch, fetchReviews, fetchReviewsBatch, fetchFeatured, voteHelpful } = window.SundayReviewsAPI;
const REVIEW_COUNT_SCALE = 26;

async function fetchGlobalForBadge(productIdsAttr) {
  const ids = (productIdsAttr || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return ids.length ? fetchSummaryBatch(ids) : fetchGlobalSummary();
}

const STAR_FILLED = `<svg class="ss-stars__star" viewBox="0 0 16 16" aria-hidden="true"><path fill="var(--ss-star)" d="M8 1l2.06 4.75L15 6.35l-3.66 3.53L12.33 15 8 12.43 3.67 15l.99-5.12L1 6.35l4.94-.6L8 1z"/></svg>`;
const STAR_EMPTY = `<svg class="ss-stars__star" viewBox="0 0 16 16" aria-hidden="true"><path fill="var(--ss-star-dim)" d="M10.36 5.43L8 0 5.64 5.43 0 6.11l4.18 4.04L3.06 16 8 13.06 12.94 16l-1.12-5.85L16 6.11l-5.64-.68zm-2.36 6.18l-3.04 1.81.69-3.49-2.51-2.42 3.46-.42L8 4.05l1.4 3.04 3.46.42-2.51 2.42.69 3.49L8 11.61z"/></svg>`;
function scaleReviewCount(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n * REVIEW_COUNT_SCALE : 0;
}

function stars(rating, lg = false) {
  const cls = lg ? "ss-stars ss-stars--lg" : "ss-stars";
  let html = `<span class="${cls}" role="img" aria-label="${rating} out of 5 stars">`;
  for (let i = 1; i <= 5; i++) html += i <= Math.round(rating) ? STAR_FILLED : STAR_EMPTY;
  html += "</span>";
  return html;
}

function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

function relativeTime(iso) {
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days < 1) return "today";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function initials(name) {
  return (name || "?").trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

function displayName(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] || "";
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
}

// Rewrite a media URL to request a smaller/lower-quality version when the
// host supports it. We never want to serve originals — thumbnails should be
// ~300px and the lightbox is capped at ~1600px @ q=80.
const VIDEO_EXT_RE = /\.(mp4|mov|m4v|webm|qt)(\?|$)/i;
function isVideoMedia(m) {
  if (!m) return false;
  if (m.kind === "video") return true;
  if (m.content_type && m.content_type.startsWith("video/")) return true;
  return VIDEO_EXT_RE.test(m.url || "");
}

// A video tile needs *something* to paint or it renders as a black box. Prefer
// a backend-supplied poster image — that lets the tile keep preload="none" (no
// video bytes fetched on load). Returns "" when no poster is available, in
// which case the caller falls back to preload="metadata" + a #t=0.1 fragment.
function videoPoster(m) {
  return (m && (m.poster_url || m.thumbnail_url || m.poster)) || "";
}

function sizedImageUrl(url, { width, quality = 80 } = {}) {
  if (!url || !width) return url;
  try {
    const u = new URL(url, window.location.origin);
    // Supabase Storage — rewrite to the image-transform endpoint to serve
    // resized/quality-reduced thumbnails instead of the full-resolution
    // originals. Requires the Image Transformations addon to be enabled on
    // the Supabase project. If transforms ever 403, the <img onerror>
    // fallback in the markup swaps to the original URL automatically.
    // Set `window.SUNDAY_REVIEWS_SUPABASE_TRANSFORMS = false` to disable.
    const transformsEnabled = window.SUNDAY_REVIEWS_SUPABASE_TRANSFORMS !== false;
    if (transformsEnabled && u.pathname.includes("/storage/v1/object/public/")) {
      u.pathname = u.pathname.replace("/storage/v1/object/public/", "/storage/v1/render/image/public/");
      u.searchParams.set("width", String(width));
      u.searchParams.set("quality", String(quality));
      u.searchParams.set("resize", "contain");
      return u.toString();
    }
    if (transformsEnabled && u.pathname.includes("/storage/v1/render/image/public/")) {
      u.searchParams.set("width", String(width));
      u.searchParams.set("quality", String(quality));
      return u.toString();
    }
    // Shopify CDN supports ?width= (and ignores unknown params gracefully).
    if (u.hostname.includes("shopify") || u.hostname.includes("cdn.shopify.com")) {
      u.searchParams.set("width", String(width));
      return u.toString();
    }
    // picsum.photos (mock) — its path is /seed/{seed}/{w}/{h}; rewrite.
    if (u.hostname.includes("picsum.photos")) {
      u.pathname = u.pathname.replace(/\/(\d+)\/(\d+)$/, `/${width}/${width}`);
      return u.toString();
    }
  } catch {}
  return url;
}

// ---- PDP widget ----------------------------------------------------------

class ReviewsWidget extends HTMLElement {
  connectedCallback() {
    this.productId = Number(this.getAttribute("product-id")) || null;
    const idsAttr = this.getAttribute("product-ids");
    this.productIds = idsAttr
      ? idsAttr.split(",").map((s) => Number(s.trim())).filter(Boolean)
      : [];
    this.currentProductId = Number(this.getAttribute("current-product-id")) || this.productId || null;
    this.writeUrl = this.getAttribute("write-url") || "/pages/write-review";
    // Not `this.title` — that is the native HTMLElement.title (tooltip) property.
    this.widgetTitle = this.getAttribute("widget-title") || "Reviews";
    // SEO identity for Product review JSON-LD (see #injectSchema). Absent on
    // non-PDP placements, in which case no schema is emitted.
    this.schemaId = this.getAttribute("schema-id") || null;
    this.schemaType = this.getAttribute("schema-type") || "Product";
    try {
      const raw = (this.getAttribute("product-map") || "").trim();
      this.productMap = raw ? JSON.parse(raw) : {};
    } catch {
      this.productMap = {};
    }
    this.state = {
      summary: null,
      reviews: [],
      page: 1,
      perPage: 5,
      sort: "newest",
      ratingFilter: null,
      hasNext: false,
      loading: true,
      mediaList: [],
      mediaSeen: new Set(),
      mediaPage: 1,
      mediaPerPage: 24,
      mediaHasNext: true,
      mediaLoading: false,
    };
    this.render();
    this.#scheduleLoad();
  }

  // Defer the initial fetches until the widget approaches the viewport.
  // PDPs that never get scrolled to the bottom (most of them) now incur
  // zero review-API egress.
  #scheduleLoad() {
    const fire = () => {
      if (this._loaded) return;
      this._loaded = true;
      this.load();
      this.loadMoreMedia();
    };
    // Search crawlers rarely scroll, so the IntersectionObserver gate would
    // hide the reviews (and their JSON-LD) from indexing. Load eagerly for
    // bots only — real users keep the egress-saving deferral below.
    const ua = (navigator.userAgent || "").toLowerCase();
    const isCrawler = /bot|crawl|spider|slurp|bingpreview|google|lighthouse|headlesschrome|duckduckbot|baiduspider|yandex/.test(ua);
    if (isCrawler || typeof IntersectionObserver === "undefined") { fire(); return; }
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          io.disconnect();
          fire();
          return;
        }
      }
    }, { rootMargin: "600px 0px" });
    io.observe(this);
  }

  fetchSummaryFn() {
    if (this.productIds.length > 1) return fetchSummaryBatch(this.productIds);
    return fetchSummary(this.productId || this.productIds[0] || this.currentProductId);
  }
  fetchReviewsFn(opts) {
    if (this.productIds.length > 1) return fetchReviewsBatch({ productIds: this.productIds, ...opts });
    return fetchReviews({ productId: this.productId || this.productIds[0] || this.currentProductId, ...opts });
  }

  async load() {
    try {
      const [summary, list] = await Promise.all([
        this.fetchSummaryFn(),
        this.fetchReviewsFn({
          page: this.state.page,
          perPage: this.state.perPage,
          sort: this.state.sort,
          rating: this.state.ratingFilter,
        }),
      ]);
      this.state.summary = summary;
      this.state.reviews = list.reviews;
      this.state.hasNext = list.has_next;
      this.state.total = list.total;
      this.state.loading = false;
      this.render();
      this.#injectSchema();
    } catch (err) {
      console.error("[ss-reviews] load failed", err);
      this.state.loading = false;
      this.state.error = true;
      this.render();
    }
  }

  // Emit schema.org Product JSON-LD (aggregateRating + a capped set of Review
  // nodes) so the reviews are eligible for Google/Bing rich-result stars.
  // Best-practice guards:
  //  - never mark up mock/fixture data (Google penalizes fake reviews),
  //  - never emit an empty/zero-count rating,
  //  - only mark up content the page actually displays.
  //  - never attribute a collection-wide aggregate to one Product entity.
  // The @id matches Shopify's structured_data output (product URL + #product)
  // so search engines merge these reviews into the server-rendered product node
  // instead of treating this as a second, incomplete merchant item.
  #injectSchema() {
    if (window.SundayReviewsAPI?.__mockEnabled) return;
    if (!this.schemaId) return;
    if (this.productIds.length > 1) return;

    const summary = this.state.summary || {};
    const count = Number(summary.review_count) || 0;
    const avg = Number(summary.average_rating) || 0;
    if (count < 1 || avg <= 0) return;

    const node = {
      "@context": "https://schema.org",
      "@type": this.schemaType,
      "@id": this.schemaId,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: Math.round(avg * 10) / 10,
        reviewCount: count,
        bestRating: 5,
        worstRating: 1,
      },
    };

    // Up to 10 of the loaded reviews, most-recent first. Only fields Google
    // recognizes; reviewBody is trimmed to keep the payload lean.
    const reviews = (this.state.reviews || [])
      .filter((r) => r && r.rating && r.body)
      .slice(0, 10)
      .map((r) => {
        const rev = {
          "@type": "Review",
          reviewRating: {
            "@type": "Rating",
            ratingValue: r.rating,
            bestRating: 5,
            worstRating: 1,
          },
          author: { "@type": "Person", name: r.reviewer_name || "Verified Buyer" },
          reviewBody: String(r.body).slice(0, 1500),
        };
        if (r.title) rev.name = r.title;
        if (r.submitted_at) {
          const d = new Date(r.submitted_at);
          if (!isNaN(d)) rev.datePublished = d.toISOString().slice(0, 10);
        }
        return rev;
      });
    if (reviews.length) node.review = reviews;

    let el = document.getElementById("ss-reviews-jsonld");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "ss-reviews-jsonld";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(node);
  }

  async loadMore() {
    if (this.state.loadingMore) return;
    this.state.loadingMore = true;
    const nextPage = this.state.page + 1;
    try {
      const list = await this.fetchReviewsFn({
        page: nextPage,
        perPage: this.state.perPage,
        sort: this.state.sort,
        rating: this.state.ratingFilter,
      });
      // Only commit the page advance after a successful fetch, so a failed
      // request doesn't permanently skip a page on the next click.
      this.state.page = nextPage;
      this.state.reviews = [...this.state.reviews, ...list.reviews];
      this.state.hasNext = list.has_next;
      this.render();
    } catch (err) {
      console.error("[ss-reviews] load more failed", err);
    } finally {
      this.state.loadingMore = false;
    }
  }

  setSort(sort) {
    this.state.sort = sort;
    this.state.page = 1;
    this.state.loading = true;
    this.state.mediaList = [];
    this.state.mediaSeen = new Set();
    this.state.mediaPage = 1;
    this.state.mediaHasNext = true;
    this.render();
    this.load();
    this.loadMoreMedia();
  }

  toggleRating(rating) {
    this.state.ratingFilter = this.state.ratingFilter === rating ? null : rating;
    this.state.page = 1;
    this.state.loading = true;
    // Reset the media gallery so it matches the active filter.
    this.state.mediaList = [];
    this.state.mediaSeen = new Set();
    this.state.mediaPage = 1;
    this.state.mediaHasNext = true;
    this.render();
    this.load();
    this.loadMoreMedia();
  }

  async loadMoreMedia() {
    const s = this.state;
    if (s.mediaLoading || !s.mediaHasNext) return;
    s.mediaLoading = true;
    try {
      const list = await this.fetchReviewsFn({
        page: s.mediaPage,
        perPage: s.mediaPerPage,
        sort: s.sort,
        rating: s.ratingFilter,
        withMedia: true,
      });
      s.mediaHasNext = !!list.has_next;
      s.mediaPage += 1;
      const before = s.mediaList.length;
      (list.reviews || []).forEach((r) => {
        (r.media || []).forEach((mm) => {
          const key = `${r.id}::${mm.url}`;
          if (s.mediaSeen.has(key)) return;
          s.mediaSeen.add(key);
          s.mediaList.push({ url: mm.url, kind: mm.kind, content_type: mm.content_type, review: r });
        });
      });
      if (s.mediaList.length !== before) {
        this.refreshMediaGallery();
        this.refreshLightboxCounter();
      }
    } catch (err) {
      console.error("[ss-reviews] media paginate failed", err);
      s.mediaHasNext = false;
    } finally {
      s.mediaLoading = false;
    }
  }

  refreshMediaGallery() {
    const host = this.querySelector("[data-media-gallery]");
    if (!host) {
      // Gallery wasn't present at first render (no media yet) — full re-render.
      this.render();
      return;
    }
    host.outerHTML = this.renderMediaGallery();
    // Only the gallery subtree was replaced — re-bind just its handlers, not the
    // whole widget (which would stack duplicate listeners on sort/load-more/helpful).
    this.bindGalleryEvents();
  }

  refreshLightboxCounter() {
    if (!this._lightbox) return;
    const { index, captionEl } = this._lightbox;
    const total = this.state.mediaList.length;
    const label = this.state.mediaHasNext ? `${total}+` : `${total}`;
    captionEl.textContent = `${index + 1} of ${label}`;
  }

  renderMediaGallery() {
    const items = this.state.mediaList;
    if (!items.length) return `<div data-media-gallery hidden></div>`;
    return `
      <div class="ss-media-gallery" data-media-gallery>
        <div class="ss-media-gallery__label">Reviews with media</div>
        <div class="ss-media-gallery__viewport">
          <button type="button" class="ss-media-gallery__nav ss-media-gallery__nav--prev" data-gallery-prev aria-label="Previous">‹</button>
          <div class="ss-media-gallery__track" data-gallery-track>
            ${items.map((m, i) => {
              const original = escapeHtml(m.url);
              if (isVideoMedia(m)) {
                const poster = videoPoster(m);
                const posterAttr = poster ? ` poster="${escapeHtml(sizedImageUrl(poster, { width: 140, quality: 50 }))}"` : "";
                // With a poster image we can stay at preload="none"; without
                // one, preload="metadata" lets #t=0.1 paint a first frame.
                const preload = poster ? "none" : "metadata";
                return `<button type="button" class="ss-media-gallery__thumb ss-media-gallery__thumb--video" data-gallery-index="${i}" aria-label="Open review video ${i + 1}">
                  <video src="${original}#t=0.1" muted playsinline preload="${preload}"${posterAttr}></video>
                  <span class="ss-media-gallery__play" aria-hidden="true">▶</span>
                </button>`;
              }
              const sized = sizedImageUrl(m.url, { width: 120, quality: 70 });
              return `<button type="button" class="ss-media-gallery__thumb" data-gallery-index="${i}" aria-label="Open review media ${i + 1}">
                <img src="${escapeHtml(sized)}" alt="" loading="lazy" data-media-url="${original}" onerror="if(this.dataset.fellBack!=='1'){this.dataset.fellBack='1';this.src=this.dataset.mediaUrl;}">
              </button>`;
            }).join("")}
          </div>
          <button type="button" class="ss-media-gallery__nav ss-media-gallery__nav--next" data-gallery-next aria-label="Next">›</button>
        </div>
      </div>
    `;
  }

  render() {
    const { summary, reviews, hasNext, loading, error, ratingFilter, sort, total } = this.state;
    let avg = summary?.average_rating || 0;
    let count = summary?.review_count || 0;
    let hist = summary?.distribution || summary?.histogram || { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    // Backend stats row may be stale (trigger hasn't recomputed). Fall back to
    // values derived from the reviews payload so the header isn't blank.
    if (!count && total) count = scaleReviewCount(total);
    if (!count && reviews?.length) count = scaleReviewCount(reviews.length);
    const histEmpty = !Object.values(hist).some((n) => n > 0);
    if (histEmpty && reviews?.length) {
      hist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
      reviews.forEach((r) => { if (hist[r.rating] != null) hist[r.rating] += 1; });
    }
    if (!avg && reviews?.length) {
      avg = reviews.reduce((s, r) => s + r.rating, 0) / reviews.length;
    }
    const histTotal = Math.max(1, Object.values(hist).reduce((a, b) => a + b, 0));
    const writeHref = this.currentProductId
      ? `${this.writeUrl}?product_id=${this.currentProductId}`
      : this.writeUrl;

    if (error) {
      this.innerHTML = `<div class="ss-reviews"><div class="ss-reviews__empty">Reviews are temporarily unavailable.</div></div>`;
      return;
    }

    this.innerHTML = `
      <div class="ss-reviews">
        ${this.widgetTitle ? `<h2 class="ss-reviews__title">${escapeHtml(this.widgetTitle)}</h2>` : ""}

        <div class="ss-reviews__header">
          <div class="ss-reviews__avg">
            <div class="ss-reviews__avg-number">${avg ? avg.toFixed(1) : "—"}</div>
            ${stars(avg, true)}
            <div class="ss-reviews__avg-count">Based on ${count.toLocaleString()} review${count === 1 ? "" : "s"}</div>
          </div>

          <div class="ss-histogram-wrap">
            <button type="button" class="ss-histogram-toggle" data-histogram-toggle aria-expanded="false" aria-controls="ss-histogram-list">
              <span class="ss-histogram-toggle__label">See rating breakdown</span>
              <span class="ss-histogram-toggle__chev" aria-hidden="true">▾</span>
            </button>
            <div class="ss-histogram" id="ss-histogram-list">
              ${[5, 4, 3, 2, 1].map((r) => {
                const c = hist[r] || 0;
                const pct = (c / histTotal) * 100;
                const pressed = ratingFilter === r;
                return `<button class="ss-histogram__row" data-rating="${r}" aria-pressed="${pressed}">
                    <span>${r}★</span>
                    <span class="ss-histogram__bar"><span class="ss-histogram__bar-fill" style="width:${pct}%"></span></span>
                    <span class="ss-histogram__count">${c}</span>
                  </button>`;
              }).join("")}
            </div>
          </div>

          <a class="button ss-reviews__cta" href="${writeHref}">Write a review</a>
        </div>

        ${this.renderMediaGallery()}

        <div class="ss-reviews__controls">
          <div>${ratingFilter ? `<button class="ss-reviews__filter-pill" aria-pressed="true" data-clear-filter>${ratingFilter}★ only ✕</button>` : ""}</div>
          <select class="ss-reviews__sort" aria-label="Sort reviews">
            <option value="newest" ${sort === "newest" ? "selected" : ""}>Newest</option>
            <option value="highest" ${sort === "highest" ? "selected" : ""}>Highest rated</option>
            <option value="lowest" ${sort === "lowest" ? "selected" : ""}>Lowest rated</option>
            <option value="helpful" ${sort === "helpful" ? "selected" : ""}>Most loved</option>
          </select>
        </div>

        <div class="ss-reviews__list">
          ${loading
            ? Array(3).fill('<div class="ss-reviews__skeleton"></div>').join("")
            : reviews.length === 0
            ? `<div class="ss-reviews__empty">No reviews yet. <a href="${writeHref}">Be the first.</a></div>`
            : reviews.map((r) => this.renderReview(r)).join("")}
        </div>

        ${hasNext && !loading ? `<div class="ss-reviews__pagination"><button class="button-secondary ss-reviews__more" data-load-more>Load more reviews</button></div>` : ""}
      </div>
    `;

    this.bindEvents();
  }

  renderProductBadge(r) {
    const pid = r.shopify_product_id || r.product_id;
    if (!pid || !this.productMap) return "";
    const info = this.productMap[pid] || this.productMap[String(pid)];
    if (!info?.title) return "";
    const label = `Reviewed: ${info.title}`;
    return info.url
      ? `<a class="ss-review__product" href="${escapeHtml(info.url)}">${escapeHtml(label)}</a>`
      : `<span class="ss-review__product">${escapeHtml(label)}</span>`;
  }

  renderReview(r) {
    const hasMedia = r.media && r.media.length > 0;
    const isSlider = hasMedia && r.media.length > 1;
    const mediaHtml = hasMedia
      ? `<div class="ss-review__media${isSlider ? " ss-review__media--slider" : ""}">
          ${isSlider ? `<button type="button" class="ss-review__media-nav ss-review__media-nav--prev" data-media-prev aria-label="Previous image">‹</button>` : ""}
          <div class="ss-review__media-track" data-media-track>
            ${r.media.map((m) => {
              const original = escapeHtml(m.url);
              if (isVideoMedia(m)) {
                const poster = videoPoster(m);
                const posterAttr = poster ? ` poster="${escapeHtml(sizedImageUrl(poster, { width: 300, quality: 60 }))}"` : "";
                // Poster image → keep preload="none" (no video bytes). No
                // poster → preload="metadata" so #t=0.1 paints a first frame
                // instead of a black tile. Click still opens the lightbox.
                const preload = poster ? "none" : "metadata";
                return `<video src="${original}#t=0.1" muted playsinline preload="${preload}"${posterAttr} data-media-url="${original}" data-media-author="${escapeHtml(r.reviewer_name)}" data-media-kind="video"></video>`;
              }
              const sized = sizedImageUrl(m.url, { width: 320, quality: 70 });
              return `<img src="${escapeHtml(sized)}" alt="Review image by ${escapeHtml(r.reviewer_name)}" loading="lazy" data-media-url="${original}" data-media-author="${escapeHtml(r.reviewer_name)}" onerror="if(this.dataset.fellBack!=='1'){this.dataset.fellBack='1';this.src=this.dataset.mediaUrl;}">`;
            }).join("")}
          </div>
          ${isSlider ? `<button type="button" class="ss-review__media-nav ss-review__media-nav--next" data-media-next aria-label="Next image">›</button>` : ""}
        </div>`
      : "";
    const contentHtml = `
      <div class="ss-review__content">
        <div class="ss-review__top">
          <span class="ss-review__name">${escapeHtml(displayName(r.reviewer_name))}</span>
          ${r.verified_buyer ? `<span class="ss-review__verified"><svg class="ss-review__verified-icon" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="8" fill="currentColor"/><path d="M4.5 8.25l2.25 2.25 4.75-5" fill="none" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>Verified buyer</span>` : ""}
          <span class="ss-review__date">${relativeTime(r.submitted_at)}</span>
        </div>
        <div class="ss-review__rating">${stars(r.rating)}</div>
        ${this.renderProductBadge(r)}
        ${r.title ? `<div class="ss-review__title">${escapeHtml(r.title)}</div>` : ""}
        <div class="ss-review__body">${escapeHtml(r.body)}</div>
        <div class="ss-review__footer">
          <button class="ss-review__helpful" data-helpful aria-label="Mark as helpful"><span class="ss-review__heart" aria-hidden="true">♥</span> <span data-helpful-count>${r.helpful_count || 0}</span></button>
        </div>
      </div>
    `;
    return `
      <article class="ss-review${hasMedia ? " ss-review--has-media" : ""}" data-review-id="${r.id}">
        <div class="ss-review__avatar" aria-hidden="true">${initials(r.reviewer_name)}</div>
        <div class="ss-review__main">
          ${contentHtml}
          ${mediaHtml}
        </div>
        ${r.merchant_reply
          ? `<div class="ss-review__reply">
              <div class="ss-review__reply-author">${escapeHtml(r.merchant_reply.replied_by)} replied</div>
              <div>${escapeHtml(r.merchant_reply.body)}</div>
            </div>`
          : ""}
      </article>
    `;
  }

  collectMedia() {
    const out = [];
    (this.state.reviews || []).forEach((r) => {
      if (!r.media || !r.media.length) return;
      r.media.forEach((m, mediaIndex) => {
        out.push({ url: m.url, kind: m.kind, content_type: m.content_type, review: r, mediaIndex });
      });
    });
    return out;
  }

  renderLightboxReviewHtml(r) {
    const verified = r.verified_buyer
      ? `<span class="ss-review__verified"><svg class="ss-review__verified-icon" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="8" fill="currentColor"/><path d="M4.5 8.25l2.25 2.25 4.75-5" fill="none" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>Verified buyer</span>`
      : "";
    return `
      <div class="ss-lightbox__review-head">
        <div class="ss-lightbox__review-name">${escapeHtml(displayName(r.reviewer_name))}</div>
        ${verified}
        <div class="ss-lightbox__review-date">${relativeTime(r.submitted_at)}</div>
      </div>
      <div class="ss-lightbox__review-rating">${stars(r.rating)}</div>
      ${this.renderProductBadge(r)}
      ${r.title ? `<div class="ss-lightbox__review-title">${escapeHtml(r.title)}</div>` : ""}
      <div class="ss-lightbox__review-body">${escapeHtml(r.body)}</div>
    `;
  }

  openLightboxAtUrl(url) {
    let media = this.state.mediaList;
    let index = media.findIndex((m) => m.url === url);
    if (index < 0) {
      // Fall back to whatever's currently visible in the cards.
      const fallback = this.collectMedia();
      const fi = fallback.findIndex((m) => m.url === url);
      if (fi < 0) return;
      // Seed mediaList with the fallback if the gallery hasn't loaded yet.
      if (!media.length) {
        fallback.forEach((m) => {
          const key = `${m.review.id}::${m.url}`;
          if (!this.state.mediaSeen.has(key)) {
            this.state.mediaSeen.add(key);
            this.state.mediaList.push(m);
          }
        });
        media = this.state.mediaList;
        index = media.findIndex((mm) => mm.url === url);
      } else {
        index = 0;
      }
    }
    this.openLightbox(index);
  }

  openLightbox(startIndex) {
    const media = this.state.mediaList;
    if (!media.length) return;
    let index = Math.max(0, Math.min(startIndex || 0, media.length - 1));

    const overlay = document.createElement("div");
    overlay.className = "ss-lightbox";
    overlay.innerHTML = `
      <div class="ss-lightbox__dialog" role="dialog" aria-modal="true">
        <button class="ss-lightbox__close" aria-label="Close">×</button>
        <div class="ss-lightbox__image-pane">
          <button class="ss-lightbox__btn ss-lightbox__btn--prev" aria-label="Previous">‹</button>
          <div class="ss-lightbox__media-slot" data-media-slot></div>
          <button class="ss-lightbox__btn ss-lightbox__btn--next" aria-label="Next">›</button>
          <div class="ss-lightbox__caption"></div>
        </div>
        <div class="ss-lightbox__review-pane" data-review-pane></div>
      </div>
    `;
    document.body.appendChild(overlay);
    document.body.style.overflow = "hidden";

    const mediaSlot = overlay.querySelector("[data-media-slot]");
    const captionEl = overlay.querySelector(".ss-lightbox__caption");
    const reviewPane = overlay.querySelector("[data-review-pane]");
    // Request images at the media slot's real size × DPR (capped at 2) instead
    // of a hard-coded 340px, so retina screens get a sharp source rather than
    // an upscale. The lightbox loads one image per open, on click only, so the
    // extra bytes are paid by engaged users — not amortized across all traffic.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const paint = () => {
      const m = media[index];
      if (isVideoMedia(m)) {
        mediaSlot.innerHTML = `
          <div class="ss-lightbox__video-wrap" data-video-wrap>
            <video class="ss-lightbox__media ss-lightbox__media--video" src="${escapeHtml(m.url)}" autoplay muted loop playsinline preload="metadata" disablepictureinpicture controlslist="nodownload noremoteplayback nofullscreen"></video>
            <button type="button" class="ss-lightbox__playpause" data-playpause aria-label="Play">
              <svg class="ss-lightbox__playpause-icon" data-icon-play viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z" fill="currentColor"/></svg>
              <svg class="ss-lightbox__playpause-icon" data-icon-pause viewBox="0 0 24 24" aria-hidden="true" hidden><path d="M6 5h4v14H6zM14 5h4v14h-4z" fill="currentColor"/></svg>
            </button>
            <button type="button" class="ss-lightbox__mute" data-mute aria-label="Unmute">
              <svg class="ss-lightbox__mute-icon" data-icon-muted viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 0 0-2.5-4.03v8.06A4.5 4.5 0 0 0 16.5 12zM14 3.23v2.06a7 7 0 0 1 0 13.42v2.06A9 9 0 0 0 14 3.23z" fill="currentColor" opacity=".35"/><path d="M3 3l18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
              <svg class="ss-lightbox__mute-icon" data-icon-sound viewBox="0 0 24 24" aria-hidden="true" hidden><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 0 0-2.5-4.03v8.06A4.5 4.5 0 0 0 16.5 12zM14 3.23v2.06a7 7 0 0 1 0 13.42v2.06A9 9 0 0 0 14 3.23z" fill="currentColor"/></svg>
            </button>
          </div>`;
        const wrap = mediaSlot.querySelector("[data-video-wrap]");
        const vid = wrap.querySelector("video");
        const btn = wrap.querySelector("[data-playpause]");
        const muteBtn = wrap.querySelector("[data-mute]");
        const iconPlay = btn.querySelector("[data-icon-play]");
        const iconPause = btn.querySelector("[data-icon-pause]");
        const iconMuted = muteBtn.querySelector("[data-icon-muted]");
        const iconSound = muteBtn.querySelector("[data-icon-sound]");
        const showState = () => {
          const playing = !vid.paused && !vid.ended;
          iconPlay.hidden = playing;
          iconPause.hidden = !playing;
          btn.setAttribute("aria-label", playing ? "Pause" : "Play");
          wrap.classList.toggle("is-playing", playing);
        };
        const showMute = () => {
          iconMuted.hidden = !vid.muted;
          iconSound.hidden = vid.muted;
          muteBtn.setAttribute("aria-label", vid.muted ? "Unmute" : "Mute");
        };
        const toggle = (e) => {
          if (e) e.stopPropagation();
          if (vid.paused || vid.ended) vid.play().catch(() => {});
          else vid.pause();
        };
        btn.addEventListener("click", toggle);
        vid.addEventListener("click", () => {
          // First tap on a muted clip: unmute (keep playing). Subsequent taps toggle play/pause.
          if (vid.muted && !vid.paused) { vid.muted = false; showMute(); return; }
          toggle();
        });
        muteBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          vid.muted = !vid.muted;
          showMute();
        });
        vid.addEventListener("play", showState);
        vid.addEventListener("pause", showState);
        vid.addEventListener("ended", showState);
        vid.addEventListener("volumechange", showMute);
        showState();
        showMute();
        // Some mobile browsers require an explicit play() call even with `autoplay muted`.
        vid.play().catch(() => {});
      } else {
        // Base the request on the larger slot dimension so neither portrait nor
        // landscape photos under-resolve (object-fit: contain fills one axis).
        const slot = Math.max(mediaSlot.clientWidth || 0, mediaSlot.clientHeight || 0) || 340;
        const reqWidth = Math.round(slot * dpr);
        const sized = sizedImageUrl(m.url, { width: reqWidth, quality: 85 });
        mediaSlot.innerHTML = `<img class="ss-lightbox__media" alt="" src="${escapeHtml(sized)}" data-media-url="${escapeHtml(m.url)}" onerror="if(this.dataset.fellBack!=='1'){this.dataset.fellBack='1';this.src=this.dataset.mediaUrl;}">`;
      }
      const totalLabel = this.state.mediaHasNext ? `${media.length}+` : `${media.length}`;
      captionEl.textContent = `${index + 1} of ${totalLabel}`;
      reviewPane.innerHTML = this.renderLightboxReviewHtml(m.review);
      if (this._lightbox) this._lightbox.index = index;
      if (index >= media.length - 3) this.loadMoreMedia();
    };
    const next = () => {
      if (index < media.length - 1) { index += 1; paint(); return; }
      if (this.state.mediaHasNext) {
        this.loadMoreMedia().then(() => {
          if (index < media.length - 1) { index += 1; paint(); }
          else if (!this.state.mediaHasNext) { index = 0; paint(); }
        });
        return;
      }
      index = 0; paint();
    };
    const prev = () => { index = (index - 1 + media.length) % media.length; paint(); };
    const close = () => {
      overlay.remove();
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
      this._lightbox = null;
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
    overlay.querySelector(".ss-lightbox__close").addEventListener("click", close);
    overlay.querySelector(".ss-lightbox__btn--prev").addEventListener("click", prev);
    overlay.querySelector(".ss-lightbox__btn--next").addEventListener("click", next);
    document.addEventListener("keydown", onKey);
    this._lightbox = { index, captionEl, mediaSlot, reviewPane };
    paint();
  }

  // Handlers for the media-gallery strip only. Called by bindEvents() on full
  // render and on its own by refreshMediaGallery() when just the strip is
  // replaced — so these never stack up on the rest of the widget.
  bindGalleryEvents() {
    this.querySelectorAll("[data-gallery-index]").forEach((btn) => {
      btn.addEventListener("click", () => this.openLightbox(Number(btn.dataset.galleryIndex)));
    });
    const galleryTrack = this.querySelector("[data-gallery-track]");
    if (galleryTrack) {
      const gprev = this.querySelector("[data-gallery-prev]");
      const gnext = this.querySelector("[data-gallery-next]");
      const step = () => Math.max(240, galleryTrack.clientWidth * 0.8);
      const updateArrows = () => {
        const max = galleryTrack.scrollWidth - galleryTrack.clientWidth - 2;
        gprev?.toggleAttribute("disabled", galleryTrack.scrollLeft <= 0);
        gnext?.toggleAttribute("disabled", galleryTrack.scrollLeft >= max);
        // Lazy-load next page when we're near the end of the strip.
        if (galleryTrack.scrollLeft >= max - 200) this.loadMoreMedia();
      };
      gprev?.addEventListener("click", () => galleryTrack.scrollBy({ left: -step(), behavior: "smooth" }));
      gnext?.addEventListener("click", () => galleryTrack.scrollBy({ left: step(), behavior: "smooth" }));
      galleryTrack.addEventListener("scroll", updateArrows, { passive: true });
      updateArrows();
    }
  }

  bindEvents() {
    this.querySelectorAll("[data-rating]").forEach((b) =>
      b.addEventListener("click", () => this.toggleRating(Number(b.dataset.rating))));
    this.querySelector("[data-clear-filter]")?.addEventListener("click", () => this.toggleRating(this.state.ratingFilter));
    const histToggle = this.querySelector("[data-histogram-toggle]");
    if (histToggle) {
      histToggle.addEventListener("click", () => {
        const wrap = histToggle.closest(".ss-histogram-wrap");
        const open = wrap.classList.toggle("is-open");
        histToggle.setAttribute("aria-expanded", open ? "true" : "false");
      });
    }
    this.querySelector(".ss-reviews__sort")?.addEventListener("change", (e) => this.setSort(e.target.value));
    this.querySelector("[data-load-more]")?.addEventListener("click", () => this.loadMore());
    this.querySelectorAll(".ss-review__media img").forEach((img) => {
      img.addEventListener("click", () => this.openLightboxAtUrl(img.dataset.mediaUrl));
    });
    // Review-card video tiles paint a poster (a backend-supplied image when
    // available, otherwise the #t=0.1 first frame via preload="metadata").
    // Wire them to the lightbox so a click loads + plays the full video —
    // click-to-play rather than streaming every tile on page load.
    this.querySelectorAll(".ss-review__media video").forEach((vid) => {
      vid.addEventListener("click", () => this.openLightboxAtUrl(vid.dataset.mediaUrl));
    });
    // The media-gallery strip ([data-media-gallery]) is the only subtree that
    // refreshMediaGallery() replaces, so its handlers live in their own method
    // and are re-bound on partial refresh without re-binding the whole widget.
    this.bindGalleryEvents();
    this.querySelectorAll(".ss-review__media--slider").forEach((slider) => {
      const track = slider.querySelector("[data-media-track]");
      const prev = slider.querySelector("[data-media-prev]");
      const next = slider.querySelector("[data-media-next]");
      const step = () => track.clientWidth || 240;
      const updateArrows = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        prev.toggleAttribute("disabled", track.scrollLeft <= 0);
        next.toggleAttribute("disabled", track.scrollLeft >= max);
      };
      prev.addEventListener("click", () => track.scrollBy({ left: -step(), behavior: "smooth" }));
      next.addEventListener("click", () => track.scrollBy({ left: step(), behavior: "smooth" }));
      track.addEventListener("scroll", updateArrows, { passive: true });
      updateArrows();
    });
    this.querySelectorAll("[data-helpful]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const reviewId = btn.closest(".ss-review")?.dataset.reviewId;
        if (!reviewId) return;
        btn.disabled = true;
        try {
          await voteHelpful(reviewId);
          const c = btn.querySelector("[data-helpful-count]");
          c.textContent = Number(c.textContent) + 1;
        } catch (err) {
          console.error("[ss-reviews] vote failed", err);
          btn.disabled = false;
        }
      });
    });
  }
}

// ---- Inline star badge ---------------------------------------------------

// localStorage cache for badge counts. Painted instantly on connect, then
// refreshed in the background. TTL is generous (24h) because the cache is
// only ever a hint — the live fetch overwrites it on the same page load.
// Version the key whenever count/rating semantics change so stale values are
// never presented as current customer-review data.
const BADGE_CACHE_PREFIX = "ssRevBadge:v3:";
const BADGE_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

function badgeCacheKey(ids) {
  return BADGE_CACHE_PREFIX + ids.slice().sort((a, b) => a - b).join(",");
}
function readBadgeCache(ids) {
  try {
    const raw = localStorage.getItem(badgeCacheKey(ids));
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || Date.now() - v.t > BADGE_CACHE_TTL_MS) return null;
    return v;
  } catch { return null; }
}
function writeBadgeCache(ids, count, avg) {
  try {
    localStorage.setItem(badgeCacheKey(ids), JSON.stringify({ c: count, a: avg, t: Date.now() }));
  } catch {}
}

class ReviewBadge extends HTMLElement {
  async connectedCallback() {
    const productId = Number(this.getAttribute("product-id")) || null;
    const idsAttr = this.getAttribute("product-ids");
    const productIds = idsAttr
      ? idsAttr.split(",").map((s) => Number(s.trim())).filter(Boolean)
      : [];
    const currentProductId = Number(this.getAttribute("current-product-id")) || productId;
    const target = this.getAttribute("target") || "#reviews";
    this.statsMode = this.getAttribute("display") === "stats";
    const cacheIds = productIds.length ? productIds : [productId || currentProductId].filter(Boolean);

    // Paint from cache first so the badge appears instantly on repeat visits.
    const cached = cacheIds.length ? readBadgeCache(cacheIds) : null;
    if (cached) this.paint(cached.c, cached.a, target);

    try {
      const summary = productIds.length > 1
        ? await fetchSummaryBatch(productIds)
        : await fetchSummary(productId || productIds[0] || currentProductId);
      let count = summary.review_count || 0;
      let avg = summary.average_rating || 0;
      // Backend stats row may be stale (trigger hasn't recomputed). Fall back
      // to a cheap reviews-list call to derive count + avg.
      if (!count) {
        try {
          const list = productIds.length > 1
            ? await fetchReviewsBatch({ productIds, page: 1, perPage: 5 })
            : await fetchReviews({ productId: productId || productIds[0] || currentProductId, page: 1, perPage: 5 });
          count = scaleReviewCount(list.total || list.reviews?.length || 0);
          if (count && list.reviews?.length) {
            avg = list.reviews.reduce((s, r) => s + r.rating, 0) / list.reviews.length;
          }
        } catch {}
      }
      if (cacheIds.length) writeBadgeCache(cacheIds, count, avg);
      // Only repaint if the fresh data differs from what we already showed.
      if (!cached || cached.c !== count || cached.a !== avg) {
        this.paint(count, avg, target);
      }
    } catch {
      // Leave the server-rendered placeholder in place on failure.
    }
  }
  paint(count, avg, target) {
    const hasReviews = Number(count) > 0 && Number(avg) > 0;
    if (!hasReviews) {
      this.innerHTML = `<a class="ss-badge" href="${target}"><span class="ss-badge__count">No reviews yet</span></a>`;
      return;
    }

    // Stats mode: "4.8 stars | 43 reviews" using only verified API values.
    if (this.statsMode) {
      const rating = Math.round(avg * 10) / 10;
      const parts = [`${rating.toFixed(1)} stars`];
      parts.push(`${count.toLocaleString()} review${count === 1 ? "" : "s"}`);
      this.innerHTML =
        `<a class="ss-badge ss-badge--stats" href="${target}">${stars(avg)}` +
        parts.map((p) => `<span class="ss-badge__stat">${p}</span>`).join("") +
        `</a>`;
      return;
    }
    this.innerHTML = `<a class="ss-badge" href="${target}">${stars(avg)}<span class="ss-badge__count">${count.toLocaleString()} review${count === 1 ? "" : "s"}</span></a>`;
  }
}

// ---- Global star badge ---------------------------------------------------

const GLOBAL_BADGE_CACHE_KEY = "ssRevBadge:__global__v6";

class GlobalReviewBadge extends HTMLElement {
  async connectedCallback() {
    const target = this.getAttribute("target") || "/pages/all-reviews";
    const showCount = this.getAttribute("show-count") !== "false";
    const labelPrefix = (this.getAttribute("label-prefix") || "").trim();

    // Paint cached value first for instant display.
    let cached = null;
    try {
      const raw = localStorage.getItem(GLOBAL_BADGE_CACHE_KEY);
      if (raw) {
        const v = JSON.parse(raw);
        if (Date.now() - v.t < BADGE_CACHE_TTL_MS) cached = v;
      }
    } catch {}
    if (cached) this.paint(cached.c, cached.a, target, showCount, labelPrefix);

    try {
      const summary = await fetchGlobalForBadge(this.getAttribute("product-ids"));
      const count = summary.review_count || 0;
      const avg = summary.average_rating || 0;
      try { localStorage.setItem(GLOBAL_BADGE_CACHE_KEY, JSON.stringify({ c: count, a: avg, t: Date.now() })); } catch {}
      if (!cached || cached.c !== count || cached.a !== avg) {
        this.paint(count, avg, target, showCount, labelPrefix);
      }
    } catch {
      // Leave the server-rendered placeholder in place on failure.
    }
  }
  paint(count, avg, target, showCount, labelPrefix) {
    const hasReviews = Number(count) > 0 && Number(avg) > 0;
    if (!hasReviews) {
      const emptyLabel = labelPrefix ? `${labelPrefix} (no reviews yet)` : "No reviews yet";
      this.innerHTML = showCount
        ? `<a class="ss-badge" href="${target}"><span class="ss-badge__count">${emptyLabel}</span></a>`
        : "";
      return;
    }
    const countText = count
      ? `(${count.toLocaleString()})`
      : "Be the first to review";
    const label = labelPrefix ? `${labelPrefix} ${countText}` : countText;
    this.innerHTML = `<a class="ss-badge" href="${target}">${stars(avg)}${showCount ? `<span class="ss-badge__count">${label}</span>` : ""}</a>`;
  }
}

// ---- Featured reviews slideshow -----------------------------------------

class FeaturedReviewsSlideshow extends HTMLElement {
  async connectedCallback() {
    this.limit = Number(this.getAttribute("limit")) || 12;
    this.spvDesktop = Number(this.getAttribute("slides-per-view-desktop")) || 3;
    this.spvTablet = Number(this.getAttribute("slides-per-view-tablet")) || 2;
    this.autoplayMs = Number(this.getAttribute("autoplay-ms")) || 0;
    this.showProductLink = this.getAttribute("show-product-link") !== "false";
    this.cardBgs = (this.getAttribute("card-bg-urls") || "").split("|").filter(Boolean);
    this.source = this.getAttribute("source") || "api";
    this.manualReviews = this.readManualReviews();
    this.swiper = null;
    this.renderShell();
    try {
      let reviews = [];
      if (this.source === "manual") {
        reviews = this.manualReviews;
      } else if (this.source === "both") {
        const apiResult = await fetchFeatured({ limit: this.limit }).catch(() => ({ reviews: [] }));
        reviews = [...this.manualReviews, ...(apiResult.reviews || [])];
      } else {
        const apiResult = await fetchFeatured({ limit: this.limit });
        reviews = apiResult.reviews || [];
      }
      if (!reviews.length) {
        this.renderEmpty();
        return;
      }
      this.renderSlides(reviews);
      this.initSwiper();
    } catch (err) {
      console.error("[ss-featured] load failed", err);
      if (this.manualReviews.length) {
        this.renderSlides(this.manualReviews);
        this.initSwiper();
      } else {
        this.renderEmpty();
      }
    }
  }

  readManualReviews() {
    const node = this.querySelector("script[data-manual-reviews]");
    if (!node) return [];
    try {
      const data = JSON.parse(node.textContent || "[]");
      return data.filter((r) => r && (r.body || r.title));
    } catch (err) {
      console.error("[ss-featured] manual reviews parse failed", err);
      return [];
    }
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  renderShell() {
    this.innerHTML = `
      <div class="ss-featured-slideshow ss-featured-slideshow--loading">
        <div class="swiper ss-featured-slideshow__swiper" data-featured-swiper>
          <div class="swiper-wrapper" data-featured-wrapper>
            <div class="swiper-slide"><div class="ss-featured-card ss-featured-card--skeleton"></div></div>
          </div>
        </div>
        <div class="ss-featured-slideshow__pagination swiper-pagination" data-featured-pagination></div>
      </div>
    `;
  }

  renderEmpty() {
    this.innerHTML = `<div class="ss-featured-slideshow"><div class="ss-featured-slideshow__empty">No featured reviews yet.</div></div>`;
  }

  renderSlides(reviews) {
    const wrapper = this.querySelector("[data-featured-wrapper]");
    wrapper.innerHTML = reviews.map((r, i) => this.renderCard(r, i)).join("");
    this.querySelector(".ss-featured-slideshow")?.classList.remove("ss-featured-slideshow--loading");
  }

  cardBgUrl(index) {
    if (!this.cardBgs.length) return "";
    return this.cardBgs[index % this.cardBgs.length];
  }

  renderCard(r, index = 0) {
    const product = this.showProductLink && r.product?.title
      ? (r.product.url
          ? `<a class="ss-featured-card__product" href="${escapeHtml(r.product.url)}">${escapeHtml(r.product.title)}</a>`
          : `<span class="ss-featured-card__product">${escapeHtml(r.product.title)}</span>`)
      : "";
    const role = r.reviewer_role ? `<div class="ss-featured-card__role">${escapeHtml(r.reviewer_role)}</div>` : "";
    const bgUrl = r.card_bg || this.cardBgUrl(index);
    const bgImg = bgUrl ? `<div class="ss-featured-card__artwork" aria-hidden="true"><img class="ss-featured-card__bg" src="${escapeHtml(bgUrl)}" alt=""></div>` : "";
    return `
      <div class="swiper-slide">
        <article class="ss-featured-card">
          ${bgImg}
          <div class="ss-featured-card__content">
            <div class="ss-featured-card__stars">${stars(r.rating)}</div>
            ${r.title ? `<h3 class="ss-featured-card__title">${escapeHtml(r.title)}</h3>` : ""}
            <p class="ss-featured-card__body">${escapeHtml(r.body)}</p>
            <div class="ss-featured-card__footer">
              <div class="ss-featured-card__name">${escapeHtml(r.reviewer_name)}</div>
              ${role}
              ${product}
            </div>
          </div>
        </article>
      </div>
    `;
  }

  initSwiper() {
    const el = this.querySelector("[data-featured-swiper]");
    if (!el || typeof Swiper === "undefined") return;
    const config = {
      // Mobile always shows a single slide.
      slidesPerView: 1,
      spaceBetween: 16,
      loop: true,
      grabCursor: true,
      pagination: {
        el: this.querySelector("[data-featured-pagination]"),
        clickable: true,
      },
      breakpoints: {
        750: { slidesPerView: this.spvTablet, spaceBetween: 20 },
        1024: { slidesPerView: this.spvDesktop, spaceBetween: 24 },
      },
    };
    if (this.autoplayMs > 0) {
      config.autoplay = { delay: this.autoplayMs, disableOnInteraction: false, pauseOnMouseEnter: true };
    }
    this.swiper = new Swiper(el, config);
  }
}

if (!customElements.get("ss-reviews-widget")) customElements.define("ss-reviews-widget", ReviewsWidget);
if (!customElements.get("ss-review-badge")) customElements.define("ss-review-badge", ReviewBadge);
if (!customElements.get("ss-global-review-badge")) customElements.define("ss-global-review-badge", GlobalReviewBadge);
if (!customElements.get("ss-featured-reviews-slideshow")) customElements.define("ss-featured-reviews-slideshow", FeaturedReviewsSlideshow);
})();
